import React from 'react';

import { StarFull, StarGray, StarHalf } from '@travel/icons/ui';

import cx from '../../utils/classnames';

import styles from './starRatingText.module.scss';

import type { ReactNode } from 'react';
type Props = {
  /**
   * Custom style for the outermost wrapper element.
   */
  className?: string;

  /**
   * The rating value to display (e.g., 4.5).
   * @type {number}
   * @minimum 0
   * @maximum 5
   */
  ratingValue?: number;

  /**
   * The size of the star icons.
   * @default 16
   */
  iconSize?: 12 | 16 | 20 | 24;

  /**
   * The color of the rating value.
   * @default "black"
   */
  ratingColor?: 'black' | 'red';

  /**
   * The number of digits to display after the decimal point in the rating value.
   * @default 1
   */
  decimalPlaces?: number;

  /**
   * An optional text string to display alongside the star rating.
   */
  text?: ReactNode;

  /**
   * A CSS class name to apply *only* to the text element (if the `text` prop is provided).
   */
  textClassName?: string;

  /**
   * A CSS class name to apply when there is no score.
   */
  noScoreClassName?: string;

  /**
   * A CSS class name to apply to the score.
   */
  scoreClassName?: string;

  /**
   * A CSS class name to apply to the full star icons.
   */
  fullStarClassName?: string;

  /**
   * A CSS class name to apply to the half star icons.
   */
  halfStarClassName?: string;

  /**
   * A CSS class name to apply to the gray star icons.
   */
  grayStarClassName?: string;

  /**
   * Whether to display the rating value as text.
   * @default true
   */
  isDisplayRating?: boolean;
  /**
   * Whether to display gray stars when the rating is 0.
   * @default false
   */
  displayGrayStar?: boolean;
};

export default function StarRatingText(props: Props) {
  const {
    className,
    ratingValue = 0,
    iconSize = 16,
    ratingColor = 'black',
    decimalPlaces = 1,
    text,
    textClassName,
    noScoreClassName,
    scoreClassName,
    fullStarClassName,
    halfStarClassName,
    grayStarClassName,
    isDisplayRating = true,
    displayGrayStar = false,
    ...rest
  } = props;

  const displayedRating = Number(ratingValue.toFixed(decimalPlaces));

  const calculateStarRating = (value: number): number => {
    if (value > 4.5) return 5;
    if (value > 4.0) return 4.5;
    if (value > 3.5) return 4;
    if (value > 3.0) return 3.5;
    if (value > 2.5) return 3;
    if (value > 2.0) return 2.5;
    if (value > 1.5) return 2;
    if (value > 1.0) return 1.5;
    if (value === 0) return 0;
    return 1;
  };

  const starRating = calculateStarRating(ratingValue);
  const ratingInt = Math.floor(starRating);
  const ratingDecimal = starRating % 1;
  const starList: Array<React.ReactNode> = [];
  const adjustedStarSize = iconSize * 0.875; // Aligning the size of the gray star with the full star

  for (let index = 0; index < 5; index += 1) {
    if (index < ratingInt) {
      starList.push(
        <StarFull
          key={index}
          size={iconSize}
          data-testid="full-star"
          className={cx(styles.fullStar, fullStarClassName)}
        />,
      );
    } else if (index === ratingInt && ratingDecimal > 0) {
      starList.push(
        <StarHalf
          key={index}
          size={adjustedStarSize}
          data-testid="half-star"
          className={halfStarClassName}
        />,
      );
    } else {
      starList.push(
        <StarGray
          key={index}
          size={adjustedStarSize}
          data-testid="gray-star"
          className={grayStarClassName}
        />,
      );
    }
  }

  const isRatingExist = ratingValue > 0 || displayGrayStar;

  return (
    <div data-testid="starRating-wrapper" className={cx(styles.starRating, className)} {...rest}>
      {isRatingExist ? (
        <>
          {starList}
          {isDisplayRating && (
            <span
              className={cx(
                styles.displayedRating,
                styles[`displayedRating--${ratingColor}`],
                scoreClassName,
              )}
              data-testid="displayed-rating"
            >
              {displayedRating}
            </span>
          )}
        </>
      ) : (
        <span className={cx(styles.noScore, noScoreClassName)} data-testid="no-score">
          -
        </span>
      )}
      {text && (
        <span className={cx(styles.text, textClassName)} data-testid="rating-text">
          {text}
        </span>
      )}
    </div>
  );
}
